//React
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageHeader from "../../../components/layout/pageHeader";
import SelectField from "../../../components/inputs/select";
import SegmentedControl from "../../../components/inputs/segmentedControl";
import Button from "../../../components/inputs/button";
import Loader from "../../../components/misc/loader";
import { formatDayLong, formatTimestamp } from "../../../components/charts/formatters";
import { statusFromError, serverMessage } from "../../../components/charts/panelStatus";
import { getPanels } from "../../../services/adminPanels";
import useVisibleInterval from "../../../hooks/useVisibleInterval";
import PanelView from "./PanelView";
import Landing from "./Landing";
import { groupByTopic, topicOf } from "./topics";
import {
	RANGE_PRESETS,
	DEFAULT_RANGE,
	rangeFor,
	rangeText,
	dataStartsText,
	regionFor,
} from "./ranges";
//CSS
import "./index.scss";

/**
 * Admin Dashboards (ASB-4377, design §6 and A.5). `/admin/dashboards` is the
 * landing page: the panels the API lists, grouped by topic, one card each.
 * `/admin/dashboards/:panelId` renders that panel from its manifest, with a
 * breadcrumb back through its topic and a picker grouped the same way.
 * Reached from the header's Admin Tools menu, which isAdmin() gates; the
 * server's 403 is the real gate, and a non-admin at either URL sees "not
 * available".
 *
 * The panel is the path; the date-range preset and region are the query
 * string, so a view can be linked from the daily email, and both carry over
 * when the picker switches panels (the region only where the new panel has
 * it; see regionFor).
 *
 * A page left open across days keeps up: the panel list (each panel's
 * `latestDay` and `computedAt`) is re-read quietly when a filter changes, when
 * the tab is shown again (unless the last read is under MIN_GAP_MS old), and
 * every REFRESH_MS while it is visible. Without that, the rows of a new day
 * showed under a banner saying the day had not been computed yet.
 *
 * The range's "yesterday" rolls over with the clock, but only together with a
 * successful read, so the range and the list's `latestDay` always come from
 * the same moment: a read that fails, or has not landed yet, never leaves a
 * banner saying a computed day is missing.
 */
export const LANDING_PATH = "/admin/dashboards";
// Hourly is enough for a daily DAG: a tab nobody touches catches the UTC
// rollover and the morning's computed day within the hour, and a tab someone
// returns to or filters re-reads at once.
export const REFRESH_MS = 60 * 60 * 1000;
export const MIN_GAP_MS = 60 * 1000;

const utcToday = () => new Date().toISOString().slice(0, 10);

function PageState({ headline, detail, children }) {
	return (
		<div className="as-dashboard-state">
			<p className="as-dashboard-state__title">{headline}</p>
			{detail ? <p>{detail}</p> : null}
			{children}
		</div>
	);
}

PageState.propTypes = {
	headline: PropTypes.string.isRequired,
	detail: PropTypes.node,
	children: PropTypes.node,
};

/** The picker's options, one react-select group per topic in API order. */
export function panelOptionGroups(panels) {
	return groupByTopic(panels).map((topic) => ({
		label: topic.title,
		options: topic.panels.map((p) => ({ value: p.id, label: p.title ?? p.id })),
	}));
}

function AdminDashboards() {
	const { panelId } = useParams();
	const [params, setParams] = useSearchParams();
	const navigate = useNavigate();
	const [panels, setPanels] = useState(null);
	// loading | ready | empty | forbidden | failed (panelStatus.js); a 404 on
	// the list means the API is not deployed yet, which is a failure here.
	const [status, setStatus] = useState("loading");
	const [error, setError] = useState(null);
	// The UTC day the range ends before; moved on only by a successful read of the list.
	const [today, setToday] = useState(utcToday);
	// Reads are numbered: a re-read lands only if it is newer than the last one
	// that landed, so a slow one never undoes a newer one, while a newer one
	// that fails doesn't throw away an older success.
	const requestRef = useRef(0);
	const appliedRef = useRef(0);
	// When the last read came back, so a return to the tab skips a list just
	// read, but never waits out a read that failed.
	const lastReadRef = useRef(0);

	const load = useCallback(async () => {
		const request = ++requestRef.current;
		setStatus("loading");
		setError(null);
		try {
			const body = await getPanels();
			lastReadRef.current = Date.now();
			if (request !== requestRef.current) return;
			appliedRef.current = request;
			const list = Array.isArray(body?.data) ? body.data : [];
			setPanels(list);
			setToday(utcToday());
			setStatus(list.length ? "ready" : "empty");
		} catch (err) {
			if (request !== requestRef.current) return;
			console.error("Admin panels unavailable", err);
			setPanels(null);
			setError(err);
			setStatus(statusFromError(err) === "forbidden" ? "forbidden" : "failed");
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	// A quiet re-read once the page is ready: no spinner, and a failure or an
	// empty answer keeps the list and the range on screen as they were.
	const refresh = useCallback(async () => {
		const request = ++requestRef.current;
		try {
			const body = await getPanels();
			lastReadRef.current = Date.now();
			if (request <= appliedRef.current) return;
			if (Array.isArray(body?.data) && body.data.length) {
				appliedRef.current = request;
				// An unchanged list keeps its objects, so a re-read with nothing new
				// re-renders nothing (an open tooltip or drill-down stays as it is),
				// except the first read after midnight UTC, which still moves the range.
				const next = JSON.stringify(body.data);
				setPanels((prev) => (JSON.stringify(prev) === next ? prev : body.data));
				setToday(utcToday());
			}
		} catch (err) {
			console.error("Admin panels refresh failed", err);
		}
	}, []);
	const ready = status === "ready";
	useVisibleInterval(refresh, REFRESH_MS, ready);
	useEffect(() => {
		if (!ready) return undefined;
		const onChange = () =>
			document.visibilityState === "visible" &&
			Date.now() - lastReadRef.current >= MIN_GAP_MS &&
			refresh();
		document.addEventListener("visibilitychange", onChange);
		return () => document.removeEventListener("visibilitychange", onChange);
	}, [ready, refresh]);
	// A filter or panel change re-reads the list as well as the rows, so the
	// banner and the "data through" stamp describe the same data as the cards.
	const viewKey = `${panelId ?? ""}?${params.toString()}`;
	const viewKeyRef = useRef(viewKey);
	useEffect(() => {
		if (viewKeyRef.current === viewKey) return;
		viewKeyRef.current = viewKey;
		if (ready) refresh();
	}, [viewKey, ready, refresh]);

	const panel = useMemo(
		() => (panelId ? (panels ?? []).find((p) => p.id === panelId) ?? null : null),
		[panels, panelId]
	);
	useEffect(() => {
		document.title = panel ? `${panel.title ?? panel.id} · Admin Dashboards` : "Admin Dashboards";
	}, [panel]);

	const rangeValue = params.get("range") ?? DEFAULT_RANGE;
	// The preset is clamped on screen to the panel's first complete day (A.6
	// `dataFrom`); the request still sends the preset, which the API clamps too.
	const dataFrom = panel?.dataFrom ?? null;
	const range = useMemo(
		() => rangeFor(rangeValue, new Date(`${today}T00:00:00Z`), dataFrom),
		[rangeValue, today, dataFrom]
	);
	const region = regionFor(panel, params.get("region"));
	const dataStarts = dataStartsText(range);

	const setParam = (key, value) => {
		const next = new URLSearchParams(params);
		if (value === null || value === undefined || value === "") next.delete(key);
		else next.set(key, value);
		setParams(next, { replace: true });
	};
	const search = params.toString() ? `?${params.toString()}` : "";
	const panelPath = (id) => ({
		pathname: `${LANDING_PATH}/${encodeURIComponent(id)}`,
		search,
	});
	const landingPath = { pathname: LANDING_PATH, search };

	let body;
	if (status === "loading") {
		body = (
			<div className="as-dashboard-state as-dashboard-state--loading">
				<Loader size="xl" />
			</div>
		);
	} else if (status === "forbidden") {
		body = (
			<PageState
				headline="Admin Dashboards are not available for your account."
				detail="They are for ArcSpan admins. If you need one of these views, ask an ArcSpan administrator."
			/>
		);
	} else if (status === "failed") {
		body = (
			<PageState
				headline="Admin Dashboards could not be loaded."
				detail={serverMessage(error) ?? "The server did not say why."}
			>
				<Button
					text="Try again"
					type="button"
					style="light"
					size="sm"
					disabled={false}
					loaded={false}
					onClick={load}
				/>
			</PageState>
		);
	} else if (status === "empty") {
		body = (
			<PageState
				headline="No dashboards have been published yet."
				detail="A dashboard appears here once its daily job has run for the first time."
			/>
		);
	} else if (!panelId) {
		body = (
			<Landing
				panels={panels}
				panelPath={panelPath}
			/>
		);
	} else if (!panel) {
		body = (
			<PageState
				headline="This dashboard is not available."
				detail={`There is no dashboard called "${panelId}".`}
			>
				<Link to={landingPath}>All Admin Dashboards</Link>
			</PageState>
		);
	} else {
		body = (
			<>
				{!panel.latestDay || (dataFrom && panel.latestDay < dataFrom) ? (
					<p className="as-dashboard-banner">No data has been computed for this dashboard yet.</p>
				) : panel.latestDay < range.to ? (
					<p className="as-dashboard-banner">
						No data for {formatDayLong(range.to)} yet. The latest day computed is{" "}
						{formatDayLong(panel.latestDay)}.
					</p>
				) : null}
				<PanelView
					key={`${panel.id}:${region}`}
					panel={panel}
					from={range.from}
					to={range.to}
					region={region}
				/>
			</>
		);
	}

	const panelGroups = panelOptionGroups(panels);
	const panelCount = panelGroups.reduce((n, g) => n + g.options.length, 0);
	const selectedPanel = panelGroups.flatMap((g) => g.options).find((o) => o.value === panel?.id);
	const rangeOptions = RANGE_PRESETS.map((p) => ({ value: p.value, label: p.label }));
	const stamp = panel?.computedAt ? formatTimestamp(panel.computedAt) : null;
	const breadcrumb = panel
		? [
				{ label: "Admin Dashboards", to: landingPath },
				{ label: topicOf(panel).title },
				{ label: panel.title ?? panel.id },
		  ]
		: undefined;

	return (
		<div
			className="wrapper admin-dashboards"
			data-status={status}
			data-view={panelId ? "panel" : "landing"}
		>
			<PageHeader
				title={panel?.title ?? "Admin Dashboards"}
				subtitle={
					panel?.description ??
					(panelId ? undefined : "Daily data panels computed by Airflow, grouped by topic.")
				}
				breadcrumb={breadcrumb}
			>
				{panel ? (
					<p className="as-dashboard-stamp p4">
						{stamp ? `Updated ${stamp}` : "Not computed yet"}
						{panel.latestDay ? ` · data through ${formatDayLong(panel.latestDay)}` : ""}
					</p>
				) : null}
			</PageHeader>
			{status === "ready" && panel ? (
				<div className="as-dashboard-filters">
					<div className="container">
						<div className="container--inner as-dashboard-filters__inner">
							<SelectField
								name="panel"
								label="Dashboard"
								width="long"
								required={false}
								disabled={false}
								search={panelCount > 8}
								options={panelGroups}
								selected={selectedPanel}
								onSubmit={(option) =>
									option && option.value !== panel.id && navigate(panelPath(option.value))
								}
							/>
							<SelectField
								name="range"
								label="Date range"
								width="medium"
								required={false}
								disabled={false}
								search={false}
								options={rangeOptions}
								selected={rangeOptions.find((o) => o.value === range.preset)}
								onSubmit={(option) => option && setParam("range", option.value)}
							/>
							{Array.isArray(panel.regions) && panel.regions.length > 1 ? (
								<div className="as-dashboard-filters__region">
									<span className="as-dashboard-filters__label">Region</span>
									<SegmentedControl
										value={region}
										options={panel.regions.map((r) => ({ label: r.toUpperCase(), value: r }))}
										optionLabel="label"
										optionValue="value"
										ariaLabel="Region"
										onChange={(value) => setParam("region", value)}
									/>
								</div>
							) : null}
							<span className="as-dashboard-filters__range p4">
								{rangeText(range)}
								{region && panel.regions?.length === 1 ? ` · ${region.toUpperCase()}` : ""}
								{dataStarts ? (
									<span
										className="as-dashboard-filters__data-starts"
										title={`This dashboard's first complete day is ${formatDayLong(
											range.dataFrom
										)}; earlier days are not shown.`}
									>
										{dataStarts}
									</span>
								) : null}
							</span>
						</div>
					</div>
				</div>
			) : null}
			<div className="container">
				<div className="container--inner as-dashboard-body">{body}</div>
			</div>
		</div>
	);
}

export default AdminDashboards;
